import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { AUTH_API_BASE_URL } from './auth-api.config';
import { Merchant } from '../models/merchant.model';

const CREDENTIAL_STORAGE_KEY = 'credential';
const TOKEN_STORAGE_KEY = 'token';

@Injectable({
    providedIn: 'root'
})
export class CredentialsService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly apiBaseUrl = inject(AUTH_API_BASE_URL);

    private readonly merchantState = signal<Merchant | null>(this.readStoredMerchant());

    readonly merchant: Signal<Merchant | null> = this.merchantState.asReadonly();
    readonly name = computed(() => this.merchantState()?.userName ?? null);

    login(userName: string, password: string): Observable<Merchant> {
        return this.http
            .post<unknown>(`${this.apiBaseUrl}/api/merchant/login`, { userName, password })
            .pipe(
                map((response) => this.extractMerchantFromLoginResponse(response)),
                tap((merchant) => this.setAuthenticatedMerchant(merchant))
            );
    }

    setPassword(oldPassword: string, newPassword: string): Observable<void> {
        const merchantId = this.merchantState()?.id;
        return this.http.post<void>(`${this.apiBaseUrl}/api/merchant/changePassword`, {
            merchantId,
            oldPassword,
            newPassword
        });
    }

    logout(): void {
        this.merchantState.set(null);
        localStorage.removeItem(CREDENTIAL_STORAGE_KEY);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        this.router.navigate(['/auth/login']);
    }

    isAuthenticated(): boolean {
        const merchant = this.merchantState();
        if (!merchant?.token?.id) {
            return false;
        }

        return !this.isTokenExpired(merchant.token.id, merchant.token.expire);
    }

    get token(): string | null {
        return this.merchantState()?.token?.id ?? null;
    }

    private setAuthenticatedMerchant(merchant: Merchant): void {
        const resolvedExpire = this.resolveTokenExpireEpochMillis(merchant.token.id, merchant.token.expire);
        const normalizedMerchant = {
            ...merchant,
            token: {
                ...merchant.token,
                expire: resolvedExpire
            }
        };

        this.merchantState.set(normalizedMerchant);
        localStorage.setItem(CREDENTIAL_STORAGE_KEY, JSON.stringify(normalizedMerchant));
        localStorage.setItem(TOKEN_STORAGE_KEY, normalizedMerchant.token.id);
    }

    private readStoredMerchant(): Merchant | null {
        const raw = localStorage.getItem(CREDENTIAL_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        try {
            const merchant = JSON.parse(raw) as Merchant;
            const resolvedExpire = this.resolveTokenExpireEpochMillis(merchant.token.id, merchant.token.expire);
            return {
                ...merchant,
                token: {
                    ...merchant.token,
                    expire: resolvedExpire
                }
            };
        } catch {
            localStorage.removeItem(CREDENTIAL_STORAGE_KEY);
            return null;
        }
    }

    private isTokenExpired(token: string, expire: number | string | undefined): boolean {
        const expireEpochMillis = this.resolveTokenExpireEpochMillis(token, expire);
        if (!expireEpochMillis) {
            return false;
        }

        return Date.now() >= expireEpochMillis;
    }

    private resolveTokenExpireEpochMillis(token: string, expire: number | string | undefined): number {
        const claimExpire = this.readJwtExpireEpochMillis(token);
        const providedExpire = this.normalizeExpireEpochMillis(expire);

        if (claimExpire && providedExpire) {
            // Backend `expire` can be behind JWT `exp` by ~24h; trust the JWT claim in that case.
            return Math.max(claimExpire, providedExpire);
        }

        return claimExpire || providedExpire;
    }

    private extractMerchantFromLoginResponse(response: unknown): Merchant {
        if (!response || typeof response !== 'object') {
            throw new Error('Invalid login response');
        }

        const maybeWrapped = response as { model?: unknown };
        const model = maybeWrapped.model ?? response;

        if (!model || typeof model !== 'object') {
            throw new Error('Invalid login response payload');
        }

        const merchant = model as Merchant;
        if (!merchant.token?.id) {
            throw new Error('Missing token in login response');
        }

        return merchant;
    }

    private normalizeExpireEpochMillis(expire: number | string | undefined): number {
        if (typeof expire === 'number') {
            // Accept both epoch seconds and epoch milliseconds.
            return expire < 1_000_000_000_000 ? expire * 1000 : expire;
        }

        if (typeof expire === 'string') {
            const asNumber = Number(expire);
            if (!Number.isNaN(asNumber)) {
                return asNumber < 1_000_000_000_000 ? asNumber * 1000 : asNumber;
            }

            const asDate = Date.parse(expire);
            if (!Number.isNaN(asDate)) {
                return asDate;
            }
        }

        return 0;
    }

    private readJwtExpireEpochMillis(token: string): number {
        try {
            const [, payload] = token.split('.');
            if (!payload) {
                return 0;
            }

            const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
            const json = atob(padded);
            const parsed = JSON.parse(json) as { exp?: number | string };

            if (typeof parsed.exp === 'number') {
                return parsed.exp * 1000;
            }

            if (typeof parsed.exp === 'string') {
                const asNumber = Number(parsed.exp);
                if (!Number.isNaN(asNumber)) {
                    return asNumber * 1000;
                }
            }

            return 0;
        } catch {
            return 0;
        }
    }
}
